import { Gender } from "@linkfluence/models/gender";
import { Platform } from "@linkfluence/models/platform";
import { InfluencerLens } from "./generic";
import {
  GenericInfluencer,
  Geo,
  InfluencerAccountMetric,
  SocialAccount,
} from ".";

type InfluencerGender = Gender.Male | Gender.Female;

export type SocialInfluencer =
  | LinkedinInfluencer
  | FacebookInfluencer
  | InstagramInfluencer
  | TwitterInfluencer
  | DailymotionInfluencer
  | WechatInfluencer
  | SinaweiboInfluencer
  | YoukuInfluencer
  | VkontakteInfluencer
  | YoutubeInfluencer
  | TwitchInfluencer
  | PinterestInfluencer
  | RedditInfluencer
  | LittleredbookInfluencer
  | BilibiliInfluencer
  | DouyinInfluencer
  | TiktokInfluencer;

export interface LinkedinInfluencer
  extends GenericInfluencer<typeof Platform.Linkedin> {
  id: string;
  idStr: string;
  permalink: string;
  name: string;
  profileImg: string;
  gender: string;
  description: string;
  occupation: string[];
  email: string[];
  geo: Geo;
  hasChildren: boolean;
  inRelationship: boolean;
}
export interface FacebookInfluencer
  extends GenericInfluencer<typeof Platform.Facebook> {
  id: string;
  idStr: string;
  verified: boolean;
  permalink: string;
  name: string;
  profileImg: string;
  gender: string;
  description: string;
  email: string[];
  geo: Geo;
  country: string;
  type: "page";
  fans: number;
  "fans-count": number;
  "talking-about-count": number;
  "checkins-count": number;
  category: string;
}

export interface InstagramInfluencer
  extends GenericInfluencer<typeof Platform.Instagram> {
  id: string;
  idStr: string;
  permalink: string;
  name: string;
  screenName: string;
  profileImg: string;
  gender: string;
  description: string;
  occupation: string[];
  socialAccount: SocialAccount;
  email: string[];
  hasChildren: boolean;
  inRelationship: boolean;
  accountType: "business";
  businessId: number;
  followers: number;
  "followings-count": number;
  "statuses-count": number;
  "followers-count": number;
}
export interface TwitterInfluencer
  extends GenericInfluencer<typeof Platform.Twitter> {
  id: string;
  idStr: string;
  verified: boolean;
  permalink: string;
  name: string;
  screenName: string;
  profileImg: string;
  gender: string;
  description: string;
  occupation: string[];
  socialAccount: SocialAccount;
  email: string[];
  geo: Geo;
  hasChildren: boolean;
  inRelationship: boolean;
  followers: number;
  "followers-count": number;
  "followings-count": number;
  "statuses-count": number;
  "favourites-count": number;
  "listed-count": number;
}
export interface DailymotionInfluencer
  extends GenericInfluencer<typeof Platform.Dailymotion> {
  id: string;
  idStr: string;
  permalinkUrl: string;
  name: string;
  profileImg: string;
  gender: string;
  socialAccount: SocialAccount;
  email: string[];
  nbVideo: number;
  views: number;
}
export interface WechatInfluencer
  extends GenericInfluencer<typeof Platform.Wechat> {
  id: string;
  idStr: string;
  name: string;
  screenName: string;
  gender: string;
  socialAccount: SocialAccount;
  email: string[];
}
export interface SinaweiboInfluencer
  extends GenericInfluencer<typeof Platform.Sinaweibo> {
  id: string;
  idStr: string;
  verified: boolean;
  permalink: string;
  name: string;
  screenName: string;
  profileImg: string;
  gender: string;
  description: string;
  occupation: string[];
  socialAccount: SocialAccount;
  email: string[];
  hasChildren: boolean;
  inRelationship: boolean;
  followers: number;
  "followers-count": number;
  "followings-count": number;
  "statuses-count": number;
  "favourites-count": number;
}
export interface YoukuInfluencer
  extends GenericInfluencer<typeof Platform.Youku> {
  id: string;
  idStr: string;
  name: string;
  gender: string;
  socialAccount: SocialAccount;
  email: string[];
}
export interface VkontakteInfluencer
  extends GenericInfluencer<typeof Platform.Vkontakte> {
  id: string;
  idStr: string;
  verified: boolean;
  permalink: string;
  name: string;
  screenName: string;
  profileImg: string;
  gender: string;
  socialAccount: SocialAccount;
  email: string[];
  typeOfUser: "page";
  followers: number;
}
export interface YoutubeInfluencer
  extends GenericInfluencer<typeof Platform.Youtube> {
  id: string;
  idStr: string;
  name: string;
  gender: InfluencerGender;
  socialAccount: SocialAccount;
  email: string[];
}
export interface TwitchInfluencer
  extends GenericInfluencer<typeof Platform.Twitch> {
  id: number;
  idStr: string;
  permalink: string;
  name: string;
  screenName: string;
  profileImg: string;
  gender: InfluencerGender;
  description: string;
  occupation: string[];
  socialAccount: SocialAccount;
  email: string[];
  geo: Geo;
  followers: number;
  views: number;
}
export interface PinterestInfluencer
  extends GenericInfluencer<typeof Platform.Pinterest> {
  id: string;
  idStr: string;
  permalink: string;
  name: string;
  screenName: string;
  profileImg: string;
  gender: InfluencerGender;
  socialAccount: SocialAccount;
  email: string[];
  geo: Geo;
  followers: number;
}
export interface RedditInfluencer
  extends GenericInfluencer<typeof Platform.Reddit> {
  type: "user" | "subreddit";
  title: string;
  url: string;
  name: string;
  screenName: string;
  profileImg: string;
  gender: string;
  description: string;
  geo: Geo;
  subscriber: number;
  karma: number;
  "awardee-karma": number;
  "awarder-karma": number;
}
export interface LittleredbookInfluencer
  extends GenericInfluencer<typeof Platform.LittleRedBook> {
  id: string;
  name: string;
  profileImg: string;
  profileType: string;
  gender: string;
  email: string;
  geo: Geo;
  age?: any;
}
export interface BilibiliInfluencer
  extends GenericInfluencer<typeof Platform.Bilibili> {
  id: string;
  name: string;
  profileImg: string;
  profileType: string;
  gender: string;
  geo: Geo;
}

export interface TiktokInfluencer
  extends GenericInfluencer<typeof Platform.Tiktok> {
  id: string;
  permalink: string;
  name: string;
  screenName: string;
  profileImg: string;
  profileType: string;
  gender: string;
  description: string;
  socialAccount: SocialAccount;
  email: string;
  geo: Geo;
  age?: any;
  followers: number;
  followings: number;
}

export interface DouyinInfluencer
  extends GenericInfluencer<typeof Platform.Douyin> {
  id: string;
  idStr: string;
  name: string;
  profileImg: string;
  profileType: string;
  gender: string;
  description: string;
  socialAccount: SocialAccount;
  email: string;
  geo: Geo;
  age?: any;
}

function formatRedditInfluencerScreenName(
  influencer: RedditInfluencer
): string {
  const prefix = influencer.type === "subreddit" ? "r/" : "u/";
  return prefix + influencer.screenName;
}

export class LinkedinLens extends InfluencerLens<LinkedinInfluencer> {
  public getId(): string {
    return `"${this.influencer.id}"`;
  }
  public getPermalink(): string {
    return this.influencer.permalink;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getDescription(): string {
    return this.influencer.description;
  }
  public getOccupation(): string {
    return this.influencer.occupation.join(", ");
  }
  public getCountry(): string {
    return this.influencer.geo.country;
  }
}
export class FacebookLens extends InfluencerLens<FacebookInfluencer> {
  public override isVerified(): boolean {
    return this.influencer.verified;
  }
  public getPermalink(): string {
    return this.influencer.permalink;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getDescription(): string {
    return this.influencer.description;
  }
  public getCountry(): string {
    return this.influencer.geo.country;
  }
  public getAccountMetrics(): InfluencerAccountMetric[] {
    return [{ key: "FANS", value: this.influencer["fans-count"] }];
  }
}
export class InstagramLens extends InfluencerLens<InstagramInfluencer> {
  public getId(): string {
    return this.influencer.screenName;
  }
  public getPermalink(): string {
    return this.influencer.permalink;
  }
  public getScreenName(): string {
    return this.influencer.screenName;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getDescription(): string {
    return this.influencer.description;
  }
  public getOccupation(): string {
    return this.influencer.occupation.join(", ");
  }
  public getAccountMetrics(): InfluencerAccountMetric[] {
    return [
      {
        key: "FOLLOWINGS",
        value: this.influencer["followings-count"],
      },
      {
        key: "FOLLOWERS",
        value: this.influencer["followers-count"],
      },
      {
        key: "PUBLICATIONS",
        value: this.influencer["statuses-count"],
      },
    ];
  }
  public getQuery(): string {
    return `(origin.platform:instagram AND author:@${this.influencer.screenName})`;
  }
}
export class TwitterLens extends InfluencerLens<TwitterInfluencer> {
  public override isVerified(): boolean {
    return this.influencer.verified;
  }
  public getPermalink(): string {
    return this.influencer.permalink;
  }
  public getScreenName(): string {
    return this.influencer.screenName;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getDescription(): string {
    return this.influencer.description;
  }
  public getOccupation(): string {
    return this.influencer.occupation.join(", ");
  }
  public getCountry(): string {
    return this.influencer.geo.country;
  }
  public getAccountMetrics(): InfluencerAccountMetric[] {
    return [
      {
        key: "FOLLOWINGS",
        value: this.influencer["followings-count"],
      },
      {
        key: "FOLLOWERS",
        value: this.influencer["followers-count"],
      },
      { key: "TWEETS", value: this.influencer["statuses-count"] },
    ];
  }
}
export class DailymotionLens extends InfluencerLens<DailymotionInfluencer> {
  public getPermalink(): string {
    return this.influencer.permalinkUrl;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getAccountMetrics(): InfluencerAccountMetric[] {
    return [{ key: "VIDEOS", value: this.influencer.nbVideo }];
  }
}
export class WechatLens extends InfluencerLens<WechatInfluencer> {
  public getName(): string {
    return this.getScreenName();
  }

  public getScreenName(): string {
    return this.influencer.screenName;
  }
}
export class SinaweiboLens extends InfluencerLens<SinaweiboInfluencer> {
  public override isVerified(): boolean {
    return this.influencer.verified;
  }
  public getPermalink(): string {
    return this.influencer.permalink;
  }
  public getScreenName(): string {
    return this.influencer.screenName;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getDescription(): string {
    return this.influencer.description;
  }
  public getOccupation(): string {
    return this.influencer.occupation.join(", ");
  }
  public getAccountMetrics(): InfluencerAccountMetric[] {
    return [
      {
        key: "FOLLOWINGS",
        value: this.influencer["followings-count"],
      },
      {
        key: "FOLLOWERS",
        value: this.influencer["followers-count"],
      },
      { key: "POSTS", value: this.influencer["statuses-count"] },
    ];
  }
}
export class YoukuLens extends InfluencerLens<LinkedinInfluencer> {}
export class VkontakteLens extends InfluencerLens<VkontakteInfluencer> {
  public override isVerified(): boolean {
    return this.influencer.verified;
  }
  public getPermalink(): string {
    return this.influencer.permalink;
  }
  public getScreenName(): string {
    return this.influencer.screenName;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getAccountMetrics(): InfluencerAccountMetric[] {
    return [{ key: "FOLLOWERS", value: this.influencer.followers }];
  }
}
export class YoutubeLens extends InfluencerLens<YoutubeInfluencer> {
  public getPermalink(): string {
    return `https://www.youtube.com/channel/${this.influencer.id}`;
  }
}
export class TwitchLens extends InfluencerLens<TwitchInfluencer> {
  public getPermalink(): string {
    return this.influencer.permalink;
  }
  public getScreenName(): string {
    return this.influencer.screenName;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getDescription(): string {
    return this.influencer.description;
  }
  public getOccupation(): string {
    return this.influencer.occupation.join(", ");
  }
  public getCountry(): string {
    return this.influencer.geo.country;
  }
  public getAccountMetrics(): InfluencerAccountMetric[] {
    return [{ key: "FOLLOWERS", value: this.influencer.followers }];
  }
}
export class PinterestLens extends InfluencerLens<PinterestInfluencer> {
  public getPermalink(): string {
    return this.influencer.permalink;
  }
  public getScreenName(): string {
    return this.influencer.screenName;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getCountry(): string {
    return this.influencer.geo.country;
  }
  public getAccountMetrics(): InfluencerAccountMetric[] {
    return [{ key: "FOLLOWERS", value: this.influencer.followers }];
  }
}
export class RedditLens extends InfluencerLens<RedditInfluencer> {
  public getId(): string {
    return this.influencer.screenName;
  }
  public getPermalink(): string {
    const prefix = this.influencer.type === "subreddit" ? "r" : "u";
    return `https://www.reddit.com/${prefix}/${this.influencer.screenName}`;
  }
  public getName(): string {
    if (this.influencer.name) {
      return this.influencer.name;
    }
    return this.influencer.screenName;
  }
  public getScreenName(): string {
    return formatRedditInfluencerScreenName(this.influencer);
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getDescription(): string {
    return this.influencer.description;
  }
  public getCountry(): string {
    return this.influencer.geo.country;
  }
  public getAccountMetrics(): InfluencerAccountMetric[] {
    return [{ key: "SUBSCRIBERS", value: this.influencer.subscriber }];
  }
  public getQuery(): string {
    if (this.influencer.type === "subreddit") {
      return `reddit.subreddit.screen-name:${this.influencer.screenName}`;
    }
    return `user.reddit.id:${this.influencer.screenName}`;
  }
}
export class LittleredbookLens extends InfluencerLens<LittleredbookInfluencer> {
  public getId(): string {
    return this.influencer.id;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getCountry(): string {
    return this.influencer.geo.country;
  }
}
export class BilibiliLens extends InfluencerLens<BilibiliInfluencer> {
  public getId(): string {
    return this.influencer.id;
  }
  public getPermalink(): string {
    return `https://space.bilibili.com/${this.influencer.id}`;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getCountry(): string {
    return this.influencer.geo.country;
  }
}

export class TiktokLens extends InfluencerLens<TiktokInfluencer> {
  public getId(): string {
    return this.influencer.id;
  }
  public getPermalink(): string {
    return this.influencer.permalink;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
  public getDescription(): string {
    return this.influencer.description;
  }
  public getCountry(): string {
    return this.influencer.geo.country;
  }
  public getAccountMetrics(): InfluencerAccountMetric[] {
    return [
      {
        key: "FOLLOWINGS",
        value: this.influencer.followings,
      },
      {
        key: "FOLLOWERS",
        value: this.influencer.followers,
      },
    ];
  }
}
export class DouyinLens extends InfluencerLens<DouyinInfluencer> {
  public getId(): string {
    return this.influencer.id;
  }
  public getAvatar(): string {
    return this.influencer.profileImg;
  }
}
